import { useEffect, useId, useRef, useState, type FormEvent } from 'react';
import { Link, useLocation } from 'react-router';
import { ApiError, api } from '../api/client.ts';
import type { Billing, Branding, UpgradeRequired } from '../api/types.ts';
import { t } from '../i18n/index.ts';
import { billingDate, isPaid } from '../lib/billing.ts';
import { useFreshBilling } from '../screens/Upgrade.tsx';
import { useStore } from '../state/store.tsx';
import { useToast } from '../state/toast.tsx';
import { Icon } from './Icon.tsx';
import { Field, SectionHeader } from './ui.tsx';
import { tierName, UpgradeNotice } from './UpgradePrompt.tsx';

/** Settings → Plan & billing: the tier, its status, usage against the limits, and the way to change it. */
export function BillingCard() {
  const { billing, status, retry } = useFreshBilling();
  const { hash } = useLocation();
  const section = useRef<HTMLElement>(null);
  const id = useId();
  useEffect(() => {
    if (hash === '#billing' && billing) section.current?.scrollIntoView({ block: 'start' });
  }, [hash, billing]);
  return (
    <section ref={section} id="billing" className="card card--pad-lg stack-md billing" aria-labelledby={id} aria-busy={status === 'loading'}>
      <SectionHeader title={t('billing.title')} id={id} />
      {billing ? (
        <BillingDetails billing={billing} />
      ) : status === 'error' ? (
        <div className="billing__problem" role="alert">
          <p className="muted-2">{t('billing.loadFailed')}</p>
          <button type="button" className="button button--sm button--outline" onClick={retry}>{t('common.retry')}</button>
        </div>
      ) : (
        <div className="skeleton billing__skeleton" />
      )}
    </section>
  );
}

function statusLine(billing: Billing): { text: string; tone: 'calm' | 'amber' } | null {
  const tier = tierName(billing.tier);
  if (billing.tier === 'none') return billing.lapsed ? { text: t('billing.lapsed'), tone: 'calm' } : null;
  if (billing.pastDue && billing.periodEnd) return { text: t('billing.pastDue', { tier, date: billingDate(billing.periodEnd) }), tone: 'amber' };
  if (billing.source === 'comp') {
    return { text: billing.periodEnd ? t('billing.source.compUntil', { date: billingDate(billing.periodEnd) }) : t('billing.source.comp'), tone: 'calm' };
  }
  if (!billing.periodEnd) return null;
  if (billing.cancelAtPeriodEnd) return { text: t('billing.ends', { date: billingDate(billing.periodEnd), tier }), tone: 'calm' };
  return { text: t('billing.renews', { date: billingDate(billing.periodEnd) }), tone: 'calm' };
}

function BillingDetails({ billing }: { billing: Billing }) {
  const toast = useToast();
  const [opening, setOpening] = useState(false);
  const line = statusLine(billing);
  const paid = isPaid(billing);
  const portal = async () => {
    setOpening(true);
    try {
      const { url } = await api.billingPortal();
      window.location.assign(url);
    } catch {
      toast.push({ tone: 'error', message: t('billing.portalError') });
      setOpening(false);
    }
  };
  const readOnly = billing.usage.readOnlyPlans;
  return (
    <>
      <div className="billing__tier">
        <span className={`billing__mark billing__mark--${billing.tier}`} aria-hidden="true"><Icon name={paid ? 'sparkle' : 'pin'} size={18} /></span>
        <span className="billing__tier-text">
          <span className="billing__tier-name">
            {billing.lapsed ? t('billing.tier.lapsed') : tierName(billing.tier)}
            {billing.interval && billing.source === 'stripe' && <span className="billing__interval">{t(`billing.interval.${billing.interval}`)}</span>}
          </span>
          {line && <span className={`billing__status ${line.tone === 'amber' ? 'billing__status--amber' : ''}`}>
            {line.tone === 'amber' && <Icon name="card" size={14} />}{line.text}
          </span>}
          {billing.source === 'revenuecat' && <span className="billing__source">{t('billing.source.revenuecat')}</span>}
        </span>
      </div>
      {!billing.enforced && (
        <p className="billing__early"><b>{t('billing.early')}</b> {t('billing.earlyBody')}</p>
      )}
      {!billing.lapsed && (
        <div className="billing__meters" aria-label={t('billing.usage')} role="group">
          <Meter label={t('billing.usage.plans')} used={billing.usage.ownedPlans} limit={billing.limits.ownedPlans} />
          <Meter label={t('billing.usage.shares')} used={billing.usage.liveShares} limit={billing.limits.liveShares} />
        </div>
      )}
      {readOnly > 0 && !billing.lapsed && (
        <p className="billing__readonly"><Icon name="lock" size={14} />
          {readOnly === 1 ? t('billing.usage.readOnlyOne') : t('billing.usage.readOnly', { n: readOnly })}
        </p>
      )}
      <div className="button-row billing__actions">
        {billing.manageable && (
          <button type="button" className="button button--outline button--flex" disabled={opening || !billing.available} onClick={() => void portal()}>
            <Icon name="card" size={15} />{opening ? t('billing.opening') : t('billing.manage')}
          </button>
        )}
        <Link to={billing.lapsed ? '/upgrade?feature=lapsed' : '/upgrade'} className={`button button--flex ${paid ? 'button--outline' : 'button--primary'}`}>
          {paid ? t('billing.seePlans') : <><Icon name="sparkle" size={15} />{billing.lapsed ? t('billing.resubscribe') : t('billing.upgrade')}</>}
        </Link>
      </div>
      {billing.tier === 'coach' && <BrandingForm current={billing.branding} />}
    </>
  );
}

function Meter({ label, used, limit }: { label: string; used: number; limit: number | null }) {
  const id = useId();
  const full = limit !== null && used >= limit;
  return (
    <div className={`meter ${full ? 'meter--full' : ''}`}>
      <div className="meter__head">
        <span id={id}>{label}</span>
        <span className="meter__value">{limit === null ? t('billing.usage.noLimit', { used }) : t('billing.usage.of', { used, limit })}</span>
      </div>
      {limit !== null && (
        <div
          className="meter__track"
          role="progressbar"
          aria-labelledby={id}
          aria-valuemin={0}
          aria-valuemax={limit}
          aria-valuenow={Math.min(used, limit)}
          aria-valuetext={t('billing.usage.label', { name: label, used, limit })}
        >
          <span className="meter__fill" style={{ width: `${limit > 0 ? Math.min(100, (used / limit) * 100) : 100}%` }} />
        </div>
      )}
    </div>
  );
}

const HEX = /^#[0-9a-f]{6}$/i;
const DEFAULT_ACCENT = '#00d4ff';

function BrandingForm({ current }: { current: Branding | null }) {
  const { refreshBilling } = useStore();
  const toast = useToast();
  const id = useId();
  const [name, setName] = useState(current?.displayName ?? '');
  const [accent, setAccent] = useState(current?.accent ?? DEFAULT_ACCENT);
  const [touched, setTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [upgrade, setUpgrade] = useState<UpgradeRequired | null>(null);
  const nameError = touched && (name.trim().length < 1 || name.trim().length > 60) ? t('billing.brand.nameError') : null;
  const accentError = touched && !HEX.test(accent) ? t('billing.brand.accentError') : null;
  const save = async (e: FormEvent) => {
    e.preventDefault();
    setTouched(true);
    if (name.trim().length < 1 || name.trim().length > 60 || !HEX.test(accent)) return;
    setBusy(true);
    try {
      await api.putBranding({ displayName: name.trim(), accent: accent.toLowerCase() });
      await refreshBilling();
      toast.push({ tone: 'success', message: t('billing.brand.saved') });
    } catch (error) {
      const needed = error instanceof ApiError ? error.upgrade : null;
      if (needed) setUpgrade(needed);
      else toast.push({ tone: 'error', message: t('billing.brand.error') });
    } finally {
      setBusy(false);
    }
  };
  const remove = async () => {
    setBusy(true);
    try {
      await api.deleteBranding();
      await refreshBilling();
      setName('');
      setAccent(DEFAULT_ACCENT);
      setTouched(false);
      toast.push({ tone: 'success', message: t('billing.brand.removed') });
    } catch {
      toast.push({ tone: 'error', message: t('billing.brand.error') });
    } finally {
      setBusy(false);
    }
  };
  const swatch = HEX.test(accent) ? accent : DEFAULT_ACCENT;
  return (
    <form className="branding stack-md" onSubmit={(e) => void save(e)} noValidate aria-labelledby={`${id}-title`}>
      <div className="stack-xs">
        <h3 className="eyebrow" id={`${id}-title`}>{t('billing.brand.title')}</h3>
        <p className="fine-print">{t('billing.brand.body')}</p>
      </div>
      <Field label={t('billing.brand.name')} htmlFor={`${id}-name`} error={nameError}>
        <input id={`${id}-name`} className="input" maxLength={60} value={name} autoComplete="organization" onChange={(e) => setName(e.target.value)} />
      </Field>
      <Field label={t('billing.brand.accent')} htmlFor={`${id}-hex`} error={accentError}>
        <div className="branding__colour">
          <input type="color" className="branding__picker" aria-label={t('billing.brand.accent')} value={swatch} onChange={(e) => setAccent(e.target.value)} />
          <input
            id={`${id}-hex`}
            className="input input--mono"
            value={accent}
            maxLength={7}
            spellCheck={false}
            autoCapitalize="off"
            aria-label={t('billing.brand.accentHex')}
            onChange={(e) => setAccent(e.target.value.trim())}
          />
        </div>
      </Field>
      {name.trim() && (
        <p className="branding__preview" style={{ borderColor: swatch }}>{t('billing.brand.preview', { name: name.trim() })}</p>
      )}
      {upgrade && <UpgradeNotice upgrade={upgrade} onDismiss={() => setUpgrade(null)} />}
      <div className="button-row">
        <button type="submit" className="button button--primary button--flex" disabled={busy}>{busy ? t('billing.brand.saving') : t('billing.brand.save')}</button>
        {current && <button type="button" className="button button--ghost button--flex" disabled={busy} onClick={() => void remove()}>{t('billing.brand.remove')}</button>}
      </div>
    </form>
  );
}
